import { trackEvent } from '@/utils/analytics'
import { useEffect, useState, useCallback, useRef } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import PageLayout from '../../components/layout/PageLayout'
import { useUIv2 } from '@/hooks/useUIv2'
import BoardListV2 from './BoardListV2'
import { Button } from '@/components/ui/button'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { useAppStore } from '@/store'
import { Search, Trash2Icon } from 'lucide-react'
import { Input } from '@/components/ui/input'
import BoardCard from './BoardCard'
import BoardTable from '@/components/BoardTable'
import AIChatAgent from '@/components/AIChatAgent'
import GettingStartedCard from '@/components/GettingStartedCard'
import AgentSelector from '@/components/AgentSelector'
import { getTimeBasedGreetingKey } from '@/lib/greeting'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { useTheme } from 'next-themes'

const BOARD_PATH = '/board'

export default function IntelligentBoard() {
  const isV2 = useUIv2()
  if (isV2) return <BoardListV2 />
  return <IntelligentBoardLegacy />
}

function IntelligentBoardLegacy() {
  const navigate = useNavigate()
  const location = useLocation()
  const { t } = useTranslation()
  const {
    loadBoardList,
    filteredBoardList,
    filterBoardList,
    createBoard,
    generateDefaultBoardName,
    loadingPreviewImages,
    deleteBoard,
    deleteBoardsBulk,
    agents,
    selectedAgent,
    isAgentsLoading,
    fetchAgents,
    setSelectedAgent,
    boardList,
    loading,
    hasStartedChatting,
    checkHasConversations,
    isCheckingConversations,
    setContextId,
  } = useAppStore()
  const [searchTerm, setSearchTerm] = useState('')
  const { user } = useAppStore()
  const { resolvedTheme } = useTheme()
  const [hasInitiallyLoaded, setHasInitiallyLoaded] = useState(false)
  const searchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const [selectedBoardIds, setSelectedBoardIds] = useState<string[]>([])
  const [showBulkDeleteConfirm, setShowBulkDeleteConfirm] = useState(false)

  // Generate welcome title using i18next
  const getWelcomeTitle = useCallback(() => {
    const firstName = user?.name?.trim()?.split(' ')[0] || ''
    return t(getTimeBasedGreetingKey(), { name: firstName })
  }, [user?.name, t])

  const welcomeTitle = getWelcomeTitle()

  useEffect(() => {
    const initializeBoardList = async () => {
      await loadBoardList()
      setHasInitiallyLoaded(true)
    }
    initializeBoardList()
  }, [loadBoardList])

  // Auto-select the last created board as context_id for chat/call features
  // Only if context_id is not already set (don't override user's selection)
  const { context_id } = useAppStore()
  useEffect(() => {
    let isMounted = true

    const autoSelectBoard = async () => {
      if (!hasInitiallyLoaded) return

      // Don't override if user already has a board selected
      if (context_id) return

      // FS-553: no longer falls back to creating a Master Board when the user
      // has no boards — the empty state handles that, and a board is created
      // from an explicit action.
      if (boardList.length > 0) {
        // Sort by created_at descending to get last created board
        const sortedBoards = [...boardList].sort((a, b) => {
          const timeB = b.created_at ? new Date(b.created_at).getTime() : 0
          const timeA = a.created_at ? new Date(a.created_at).getTime() : 0
          return timeB - timeA
        })
        const lastCreatedBoard = sortedBoards[0]
        if (lastCreatedBoard && isMounted) {
          setContextId(lastCreatedBoard.id)
        }
      }
    }

    autoSelectBoard()

    return () => {
      isMounted = false
    }
  }, [hasInitiallyLoaded, boardList, context_id, setContextId])

  // Fetch agents from store
  useEffect(() => {
    fetchAgents()
  }, [fetchAgents])

  // Check if user has started chatting (to hide Getting Started panel)
  // Re-check on navigation to handle conversation deletion scenario
  useEffect(() => {
    checkHasConversations()
  }, [checkHasConversations, location.pathname])


  const goToBoard = (id: string) => {
    trackEvent('workspace-switched', { workspace_id: id })
    navigate(`${BOARD_PATH}/${id}`)
  }

  const handleCreateBoard = async () => {
    await loadBoardList()
    const defaultName = generateDefaultBoardName()
    const board = await createBoard(defaultName)
    if (board) {
      trackEvent('workspace-created', { workspace_id: board.id })
      goToBoard(board.id)
    }
  }

  const handleDeleteBoard = async (id: string) => {
    const success = await deleteBoard(id)
    if (success) {
      trackEvent('workspace-deleted', { workspace_id: id })
    }
  }

  const handleRowSelect = useCallback((id: string, checked: boolean) => {
    setSelectedBoardIds(prev =>
      checked ? [...prev, id] : prev.filter(bid => bid !== id)
    )
  }, [])

  const handleSelectAllBoards = useCallback((checked: boolean) => {
    if (checked) {
      const selectableIds = filteredBoardList.map(b => b.id)
      setSelectedBoardIds(selectableIds)
    } else {
      setSelectedBoardIds([])
    }
  }, [filteredBoardList])

  const handleBulkDelete = useCallback(async () => {
    setShowBulkDeleteConfirm(false)
    const { deletedCount, failedIds } = await deleteBoardsBulk(selectedBoardIds)
    for (const id of selectedBoardIds) {
      if (!failedIds.includes(id)) {
        trackEvent('workspace-deleted', { workspace_id: id })
      }
    }
    if (deletedCount > 0) {
      toast.success(t('board:actions.bulk_delete_success', { count: deletedCount }))
    }
    if (failedIds.length > 0) {
      toast.error(t('board:actions.bulk_delete_error', { count: failedIds.length }))
    }
    setSelectedBoardIds(failedIds)
  }, [selectedBoardIds, deleteBoardsBulk, t])

  const handleBoardSearch = (value: string) => {
    setSearchTerm(value)
    setSelectedBoardIds([])
    filterBoardList(value)

    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current)
    }
    searchDebounceRef.current = setTimeout(() => {
      if (value.trim()) {
        trackEvent('board-searched', { query_length: value.trim().length })
      }
    }, 300)
  }

  const handleSendMessage = useCallback(async (message: string) => {
    // Find the latest board (sort by updated_at descending).
    let targetBoard = boardList.length > 0
      ? [...boardList].sort((a, b) => {
          const timeB = b.updated_at ? new Date(b.updated_at).getTime() : 0
          const timeA = a.updated_at ? new Date(a.updated_at).getTime() : 0
          return timeB - timeA
        })[0]
      : null

    // FS-553: no board yet — create the user's first board on demand
    // (a normal, named board) instead of the old auto-created Master Board.
    if (!targetBoard) {
      const newBoard = await createBoard(generateDefaultBoardName())
      if (newBoard) {
        targetBoard = newBoard
      } else {
        // Board creation failed, cannot proceed
        return
      }
    }

    navigate(`/board/${targetBoard.id}`, {
      state: { initialMessage: message, autoSend: true }
    })
  }, [boardList, createBoard, generateDefaultBoardName, navigate])

  return (
    <PageLayout
      title={welcomeTitle}
      bgColor="bg-white dark:bg-dark-card"
      bgStyle={{
        background: resolvedTheme === 'dark'
          ? 'linear-gradient(170deg, #4A2D1A 0%, #352015 15%, #1A1816 45%, #1A1816 100%)'
          : 'linear-gradient(170deg, #fed3cb 0%,  #ffddc2 15%, #ffffff 45%, #ffffff 100%)',
      }}
      welcomePage
    >
      <>
        <AIChatAgent
          selectedAgent={selectedAgent}
          onSendMessage={handleSendMessage}
        />
        {/* Main Content Area with Column Layout */}
        <div className="grid grid-cols-12 gap-6 mb-8 mt-2">
          {/* Agent Selector and Chat - 8 columns */}
          <div className="col-span-8">
            {/* Agent Selector */}
            {!isAgentsLoading && selectedAgent && (
              <AgentSelector
                agents={agents}
                selectedAgent={selectedAgent}
                onAgentSelect={setSelectedAgent}
              />
            )}
            {isAgentsLoading && (
              <div className="flex items-center justify-center p-8">
                <div className="text-gray-500 dark:text-dark-text-muted">{t('board:loading_text')}</div>
              </div>
            )}

            <h3 className="text-lg font-semibold mt-10 mb-4">{t('board:recent_boards_text')}</h3>

            <div
              className={
                filteredBoardList.length > 0
                  ? 'flex gap-6'
                  : 'w-full flex justify-center mt-10'
              }
            >
              {filteredBoardList.length > 0 ? (
                filteredBoardList
                  .slice(0, 3)
                  .map((item) => (
                    <BoardCard
                      key={item.id}
                      board={item}
                      handleClick={goToBoard}
                      loading={loadingPreviewImages}
                    />
                  ))
              ) : (
                <h2 className="text-gray-400 dark:text-gray-500">{t('board:empty_text')}</h2>
              )}
            </div>
          </div>

          {/* Getting Started Card - 4 columns (hidden after first chat or while loading) */}
          {!isCheckingConversations && !hasStartedChatting && (
            <div className="col-span-4">
              <GettingStartedCard />
            </div>
          )}
        </div>

        {/* Page Header */}
        <div className="flex justify-between items-center mb-4">
          <h1 className="text-2xl font-bold">{t('board:title')}</h1>
          <div className="flex gap-6 items-center">
            {selectedBoardIds.length > 0 && (
              <Button
                variant="destructive"
                size="sm"
                onClick={() => setShowBulkDeleteConfirm(true)}
                className="flex items-center gap-2"
              >
                <Trash2Icon className="w-4 h-4" />
                {t('board:actions.bulk_delete_button', { count: selectedBoardIds.length })}
              </Button>
            )}
            <div className="relative w-72">
              <Search className="absolute start-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400 dark:text-gray-500" />
              <Input
                placeholder={t('board:search_boards')}
                value={searchTerm}
                onChange={(e) => handleBoardSearch(e.target.value)}
                className="ps-10"
              />
            </div>
            <Button onClick={handleCreateBoard} disabled={loading} data-tour-id="board-create">{t('board:new_board')}</Button>
          </div>
        </div>

        {/* All Boards Table */}
        <div className="mt-0">
          <BoardTable
            boards={filteredBoardList}
            onBoardClick={goToBoard}
            onBoardDelete={handleDeleteBoard}
            loading={loadingPreviewImages}
            selectable
            selectedRows={selectedBoardIds}
            onRowSelect={handleRowSelect}
            onSelectAll={handleSelectAllBoards}
          />
        </div>

        <AlertDialog open={showBulkDeleteConfirm} onOpenChange={setShowBulkDeleteConfirm}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>
                {t('board:actions.bulk_delete_title', { count: selectedBoardIds.length })}
              </AlertDialogTitle>
              <AlertDialogDescription>
                {t('board:actions.bulk_delete_description')}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>{t('common:buttons.cancel')}</AlertDialogCancel>
              <AlertDialogAction onClick={handleBulkDelete}>
                {t('common:buttons.delete')}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </>
    </PageLayout>
  )
}
